import { useEffect, useState } from 'react';
import {
  type ApiError,
  type GapAddressCommitResult,
  type ServedGap,
  commitGapAddress,
  gapAddressTurn,
  startGapAddress,
} from '../../lib/api';
import {
  type GapAnswerMode,
  DEFAULT_GAP_ANSWER_MODE,
  GAP_ANSWER_MODES,
  describeGapAnswerMode,
} from '../../lib/gapAnswerMode';
import { type TermCorrection, describeCorrection, repairTerms, revertCorrection } from '../../lib/termRepair';
import { VoiceTextarea } from '../VoiceTextarea';
import { GapVoiceInterview } from './GapVoiceInterview';
import styles from './GapAddressPanel.module.css';

// =============================================================================
// GapAddressPanel — the scoped conversation (docs/design/gap-address-loop-
// 2026-07-05.md, the owner's ruling #1): "I wanted to do a click on
// something and say, I'd like to address that, and have a quick
// conversation with Mira that makes sense."
//
// THIN PRESENTATION: every question, class label, and outcome rendered here
// is exactly what the server served (startGapAddress / gapAddressTurn /
// commitGapAddress) — this component classifies nothing, extracts nothing,
// and never fabricates a rescore result. The "rescoring against this role…"
// wait state below is real: it's the SAME network call as the commit
// (jobs.routes.ts's POST /gap-address/commit fires the rescore inline and
// the panel simply stays in its 'committing' phase until that single
// request resolves — no separate poll).
//
// W22 (2026-07-24) — THREE ANSWER PATHS, his spec verbatim: "the address this
// needs to be a two-way voice interview interaction as an option". The mode row
// below offers type · dictate · talk it through. All three converge on the SAME
// send path (`handleSend` -> gapAddressTurn -> commitGapAddress) — the voice
// loop is a different way to SAY the answer, never a different way to save it,
// and it carries `source: 'voice'` so the provenance travels with the words.
// Nothing auto-starts a microphone: 'type' is the default and the live session
// opens only on an explicit tap.
//
// NO LONG FORMS: one VoiceTextarea (mic icon per rule #2) per turn. The
// conversation is capped server-side (MAX_GAP_ADDRESS_TURNS) — this panel
// just renders whatever `readyToClose` the server reports and offers the
// commit action once it's true, or lets the user commit early at any point
// (the owner's "might not have done exactly that, but I did these things in
// a similar capacity" framing is a CONVERSATION, not a rigid form the user
// is locked into finishing).
// =============================================================================

type Phase = 'opening' | 'chatting' | 'sending_turn' | 'committing' | 'closed' | 'error';

interface Turn {
  role: 'assistant' | 'user';
  text: string;
}

export function GapAddressPanel({
  jobId,
  gap,
  onClose,
  onAddressed,
}: {
  jobId: string;
  gap: ServedGap;
  /** Called when the user closes the panel WITHOUT committing (backdrop tap,
   *  the X, or a dismiss) — the caller just hides the panel, nothing to
   *  refresh. */
  onClose: () => void;
  /** Called once a commit (or dismiss-via-server) round-trip resolves — the
   *  caller re-renders the gap list + score breakdown from the SERVED
   *  result (never re-derives "addressed" locally). */
  onAddressed: (result: GapAddressCommitResult) => void;
}) {
  const [phase, setPhase] = useState<Phase>('opening');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [readyToClose, setReadyToClose] = useState(false);
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<GapAddressCommitResult | null>(null);
  const [mode, setMode] = useState<GapAnswerMode>(DEFAULT_GAP_ANSWER_MODE);
  /** His own vocabulary for THIS gap, served at session open. Empty is honest
   *  (thin evidence) — the repair pass then runs on the curated lexicon alone. */
  const [knownTerms, setKnownTerms] = useState<string[]>([]);
  /** Every term repair applied to dictated/spoken text, shown so nothing is a
   *  silent rewrite. Cleared once an answer is sent. */
  const [repairs, setRepairs] = useState<TermCorrection[]>([]);
  /** The spoken answer is handed back here for review before it is sent — his
   *  words, editable, his edit always winning. */
  const [awaitingSpokenConfirm, setAwaitingSpokenConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    startGapAddress(jobId, gap.id)
      .then((r) => {
        if (cancelled) return;
        setSessionId(r.sessionId);
        setTurns([{ role: 'assistant', text: r.question }]);
        setReadyToClose(r.readyToClose);
        setKnownTerms(r.knownTerms ?? []);
        setPhase('chatting');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(describeGapAddressError(err));
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, gap.id]);

  async function handleSend() {
    if (!sessionId || !answer.trim() || phase === 'sending_turn') return;
    const said = answer.trim();
    // Provenance travels with the words (the InputCapture seam, apps/api/lib/
    // capture.ts): typed stays 'text'; anything the microphone produced —
    // dictated into the box or spoken in the live loop — is 'voice'.
    const source = mode === 'type' ? 'text' : 'voice';
    setTurns((prev) => [...prev, { role: 'user', text: said }]);
    setAnswer('');
    setRepairs([]);
    setAwaitingSpokenConfirm(false);
    setPhase('sending_turn');
    setError(null);
    try {
      const r = await gapAddressTurn(jobId, sessionId, { text: said, source });
      setTurns((prev) => [...prev, { role: 'assistant', text: r.question }]);
      setReadyToClose(r.readyToClose);
      setPhase('chatting');
    } catch (err) {
      setError(describeGapAddressError(err));
      setPhase('error');
    }
  }

  /** The live loop handing back HIS words (already guarded by
   *  guardSpokenCommit inside GapVoiceInterview — an agent line can never
   *  arrive here). They land in the SAME answer box the typed path uses, so he
   *  reads them, edits anything the mic got wrong, and sends through the SAME
   *  handleSend. Nothing is committed straight off the transcript. */
  function handleSpokenAnswer(spoken: string) {
    setAnswer(spoken);
    setAwaitingSpokenConfirm(true);
    setError(null);
  }

  /** The dictation post-pass: repair only toward KNOWN terms, and record every
   *  change for the audit strip. Never touches typed text. */
  function handleDictatedChunk(chunk: string): string {
    const report = repairTerms(chunk, { corpusTerms: knownTerms });
    if (report.corrections.length > 0) {
      setRepairs((prev) => [...prev, ...report.corrections]);
    }
    return report.corrected;
  }

  function handleUndoRepair(correction: TermCorrection) {
    setAnswer((prev) => revertCorrection(prev, correction));
    setRepairs((prev) => prev.filter((c) => c !== correction));
  }

  async function handleCommit() {
    if (!sessionId || phase === 'committing') return;
    setPhase('committing');
    setError(null);
    try {
      const result = await commitGapAddress(jobId, { sessionId });
      if (!result.ok) {
        setError(result.message ?? "That one didn't quite land — try saying a bit more.");
        setPhase('chatting');
        return;
      }
      setCommitResult(result);
      setPhase('closed');
      onAddressed(result);
    } catch (err) {
      setError(describeGapAddressError(err));
      setPhase('chatting');
    }
  }

  async function handleDismiss() {
    if (!sessionId) {
      onClose();
      return;
    }
    try {
      const result = await commitGapAddress(jobId, { sessionId, dismiss: true });
      onAddressed(result);
    } catch {
      // Dismissal is best-effort — the user is leaving either way.
    }
    onClose();
  }

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.headerRow}>
          <span
            className={`${styles.classChip} ${
              gap.gapClass === 'evidence' ? styles.classChipEvidence : styles.classChipSituation
            }`}
          >
            {gap.gapClass === 'evidence' ? 'Missing evidence' : 'Your current situation'}
          </span>
          <button type="button" className={styles.closeButton} onClick={handleDismiss} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.gapText}>{gap.text}</div>

        {phase === 'opening' ? (
          <div className={styles.statusRow}>
            <span className={styles.spinner} />
            Meira's reading the gap…
          </div>
        ) : (
          <div className={styles.transcript}>
            {turns.map((t, i) => (
              <div key={i} className={t.role === 'assistant' ? styles.turnMira : styles.turnUser}>
                {t.text}
              </div>
            ))}
            {phase === 'sending_turn' ? (
              <div className={styles.statusRow}>
                <span className={styles.spinner} />
                Thinking…
              </div>
            ) : null}
          </div>
        )}

        {error ? <div className={styles.errorNote}>{error}</div> : null}

        {phase === 'closed' && commitResult ? (
          <ClosedState result={commitResult} gapClass={gap.gapClass} onClose={onClose} />
        ) : phase !== 'opening' ? (
          <>
            {/* W22 — the three answer paths. An explicit, user-chosen option:
                nothing here starts a microphone on its own. */}
            <div className={styles.modeRow} role="group" aria-label="How do you want to answer?">
              {GAP_ANSWER_MODES.map((m) => {
                const meta = describeGapAnswerMode(m);
                return (
                  <button
                    key={m}
                    type="button"
                    className={`${styles.modeButton} ${mode === m ? styles.modeButtonActive : ''}`}
                    aria-pressed={mode === m}
                    onClick={() => {
                      setMode(m);
                      setError(null);
                      if (m !== 'talk') setAwaitingSpokenConfirm(false);
                    }}
                    disabled={phase === 'sending_turn' || phase === 'committing'}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
            <div className={styles.modeHint}>{describeGapAnswerMode(mode).hint}</div>

            {mode === 'talk' && !awaitingSpokenConfirm ? (
              <GapVoiceInterview
                jobId={jobId}
                gapText={gap.text}
                corpusTerms={knownTerms}
                onAnswerReady={handleSpokenAnswer}
              />
            ) : (
              <>
                <VoiceTextarea
                  label={awaitingSpokenConfirm ? 'What you said' : 'Your answer'}
                  value={answer}
                  onChange={setAnswer}
                  placeholder={
                    gap.gapClass === 'evidence'
                      ? "Tell Meira the closest thing you actually did do…"
                      : 'Tell Meira where things stand today…'
                  }
                  rows={3}
                  disabled={phase === 'sending_turn' || phase === 'committing'}
                  onFinalChunkTransform={mode === 'dictate' ? handleDictatedChunk : undefined}
                />
                {repairs.length > 0 ? (
                  <div className={styles.repairStrip}>
                    <span className={styles.repairLead}>
                      I fixed what the mic misheard, against words already known. Undo anything I
                      got wrong — your version wins.
                    </span>
                    {repairs.map((c, i) => (
                      <span key={`${c.replacement}-${i}`} className={styles.repairItem}>
                        {describeCorrection(c)}
                        <button
                          type="button"
                          className={styles.repairUndo}
                          onClick={() => handleUndoRepair(c)}
                        >
                          undo
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </>
            )}

            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleSend}
                disabled={!answer.trim() || phase === 'sending_turn' || phase === 'committing'}
              >
                Send
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleCommit}
                disabled={phase === 'committing' || turns.filter((t) => t.role === 'user').length === 0}
              >
                {phase === 'committing' ? 'Rescoring against this role…' : readyToClose ? 'Save this' : 'Save anyway'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function ClosedState({
  result,
  gapClass,
  onClose,
}: {
  result: GapAddressCommitResult;
  gapClass: 'evidence' | 'situation';
  onClose: () => void;
}) {
  return (
    <>
      <div className={styles.doneNote}>
        {result.committed
          ? gapClass === 'evidence'
            ? "Saved to your corpus — that's on record now, tied to this gap, so it won't come up blind again."
            : "Got it — I've noted where things stand today, dated, tied to this gap."
          : "Nothing saved this time — that gap's still open."}
        {result.rescored ? ' Your score for this role has been updated.' : ''}
        {result.rescoreError ? ` (Couldn't rescore just now: ${result.rescoreError})` : ''}
      </div>
      {result.nextUnaddressed ? (
        <div className={styles.nextGapNote}>
          Another gap is still open: "{result.nextUnaddressed.text}" — you can address it next from the list.
        </div>
      ) : (
        <div className={styles.nextGapNote}>Every named gap on this score has been addressed.</div>
      )}
      <button type="button" className={styles.primaryButton} onClick={onClose}>
        Done
      </button>
    </>
  );
}

function describeGapAddressError(err: unknown): string {
  const apiErr = err as ApiError;
  if (apiErr?.status === 503) return "Meira isn't ready to talk right now — try again in a bit.";
  if (apiErr?.status === 409) return 'This conversation has already been closed.';
  return err instanceof Error ? err.message : 'Something went wrong — try again.';
}
